/**
 * Setup Account Page
 *
 * For new users to set their password after being invited by an admin.
 */

import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { setupAccount, loginWithGoogle } from '../../api/auth';
import { getErrorMessage } from '../../api/client';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';

const schema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export default function SetupAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-4">
        <div className="max-w-md w-full bg-white p-8 text-center border border-hairline">
          <div className="w-16 h-16 bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Setup link missing
          </h2>
          <p className="text-gray-600 mb-6">
            This page expects a setup token in the URL. The link in your
            invitation email may have been truncated. Try opening it again, or
            contact your administrator for a fresh invite.
          </p>
          <Link
            to="/login"
            className="text-primary-600 hover:text-primary-500 font-medium"
          >
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: FormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await setupAccount({
        token,
        password: data.password,
        confirmPassword: data.confirmPassword,
      });
      // User is now logged in, redirect based on org role
      // Check if user has admin/owner role in any org
      const hasAdminRole = response.user.orgs?.some(
        (org) => org.role === 'ADMIN' || org.role === 'OWNER'
      );
      if (hasAdminRole) {
        navigate('/admin');
      } else {
        navigate('/crew');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome to ProgramRow
          </h1>
          <p className="mt-2 text-gray-600">
            Set your password to complete account setup.
          </p>
        </div>

        <div className="bg-white p-8 border border-hairline">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                autoFocus
                {...register('password')}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack ${errors.password ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="At least 8 characters"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'}`}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rack disabled:opacity-75"
            >
              {isSubmitting ? 'Setting up...' : 'Complete Setup'}
            </button>
          </form>

          {/* Invited users can skip password creation entirely: a verified
              Google sign-in with the invited email activates the account
              server-side. Renders nothing when Google auth isn't configured. */}
          <GoogleSignInButton
            text="continue_with"
            onCredential={async (idToken) => {
              setError(null);
              try {
                const response = await loginWithGoogle(idToken);
                const hasAdminRole = response.user.orgs?.some(
                  (org) => org.role === 'ADMIN' || org.role === 'OWNER'
                );
                navigate(hasAdminRole ? '/admin' : '/crew');
              } catch (err) {
                setError(getErrorMessage(err));
              }
            }}
          />
          <p className="mt-3 text-center text-xs text-gray-500">
            Signing in with Google uses the email address your invite was sent to.
          </p>
        </div>
      </div>
    </div>
  );
}
